import React, { useCallback, useEffect, useState } from 'react'
import { CardContainer, IconEditPen, Avatar, IconArrowDown, IconCheckCircle, IconCloseOutlined } from '@tutorbot/ui-components'
import { Modal, Table, Menu, Dropdown } from 'antd'
import qs from 'qs'
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE_SIZE_OPTIONS } from '../../services/tableSort'
import { userDataList, usersDataCount, updateUser, requestPasswordReset, listRoles } from '../../services/api'
import { Alert, resetPasswordConfirmation, userDeleteConfirmation, userRoleChangeConfirmation } from '../UI/Modal/ModalContent'
import { resendUsersInvite, deleteUser } from '../../services/api/ums/user'
import NoDataAvailable from '../../components/UI/NoDataAvailable/NoDataAvailable'
import { useSelector } from 'react-redux'
import styles from './UserDataList.module.css'

const UserDataList = () => {
  const [data, setData] = useState([])
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [userDataTotal, setUserDataTotal] = useState(DEFAULT_PAGE_SIZE)
  const [userName, setUserName] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [userGuid, setUserGuid] = useState(null)
  const [roles, setRoles] = useState([])
  const [usersList, setUsersList] = useState([])
  const lastUpdatedAt = useSelector((state) => state.userLastUpdatedAtEvent.listLastUpdatedAt)

  useEffect(() => {
    const typesToKeep = ['admin', 'analyst']

    listRoles().then(({ data }) => {
      setRoles(data.roles
        .filter(({ type }) => typesToKeep.includes(type))
        .map(({ id, name }) => {
          return {
            label: name,
            onClick: changeUserRoleHandler,
            id
          }
        }))
    })
  }, [])

  const activateUserHandler = (activate = true, guid) => {
    const q = {
      blocked: activate ? 0 : 1
    }

    updateUser(guid, q)
      .then(res => {
        const icon = <IconCheckCircle size={'sm'} fill={'var(--success-color)'} />
        const title = activate ? 'Account reactivated' : 'Account deactivated'
        const content = <p>Account has been successfully {activate ? 'reactivated' : 'deactivated'}</p>

        Alert(title, content, icon)
      })
      .catch(() => {
        const icon = <IconCloseOutlined size={'sm'} fill={'var(--error-color)'} />
        const title = 'Error'
        const content = <p>Something went wrong.</p>

        Alert(title, content, icon)
      })
      .finally(() => {
        getUsersDataList()
      })
  }

  const resendInvitationHandler = (_, guid) => {
    resendUsersInvite(guid)
      .then((res) => {
        const icon = <IconCheckCircle size={'sm'} fill={'var(--success-color)'} />
        const title = 'Invite sent'
        const content = <p>Invite has been successfully sent.</p>

        Alert(title, content, icon)
      })
      .catch((error) => {
        const icon = <IconCloseOutlined size={'sm'} fill={'var(--error-color)'} />
        const title = 'Error'
        const content = <p>{error.response.data.message}</p>

        Alert(title, content, icon)
      })
      .finally(() => {
        getUsersDataList()
      })
  }

  const resetPasswordHandler = () => {
    resetPasswordConfirmation(userName, handlePasswordReset)
  }

  const userDeleteHandler = () => {
    userDeleteConfirmation(userName, handleUserDelete)
  }

  const changeUserRoleHandler = (roleId, guid) => {
    userRoleChangeConfirmation(userName, () => handleUserRoleChange(roleId, guid))
  }

  const handlePasswordReset = () => {
    requestPasswordReset(userEmail)
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        Modal.destroyAll()
        getUsersDataList()
      })
  }

  const handleUserDelete = () => {
    deleteUser(userGuid)
      .catch((error) => {
        console.log(error)
      })
      .finally(() => {
        Modal.destroyAll()
        getUsersDataList()
      })
  }

  const handleUserRoleChange = useCallback(async (roleId, guid) => {
    await updateUser(guid, { role: roleId })
      .finally(() => {
        getUsersDataList()
      })
  }, [data])

  const statusMenu = [
    {
      label: 'Deactivate account',
      onClick: activateUserHandler
    }, {
      label: 'Reactivate account',
      onClick: activateUserHandler
    }, {
      label: 'Resend invite',
      onClick: resendInvitationHandler
    }, {
      label: 'Registered',
      onClick: resendInvitationHandler
    }
  ]

  const resetAndDeleteMenu = [
    {
      label: 'Reset password',
      onClick: resetPasswordHandler
    }, {
      label: 'Delete user',
      onClick: userDeleteHandler
    }
  ]

  const getQueryString = useCallback(() => {
    const q = {
      _limit: pageSize,
      _start: (page - 1) * pageSize,
      _sort: 'created_at:desc'
    }

    return qs.stringify(q)
  }, [pageSize, page])

  const getUsersDataList = useCallback(() => {
    const filter = getQueryString()
    userDataList(filter)
      .then(res => setData(res.data))
      .catch(error => {
        console.log(error)
      })
  }, [getQueryString])

  const getUsersTotal = useCallback(() => {
    const filter = getQueryString()
    usersDataCount(filter)
      .then(res => setUserDataTotal(res.data))
      .catch(error => {
        console.log(error)
      })
  }, [getQueryString])

  const onTablePaginationChange = (page, pageSize) => {
    setPage(page)
    setPageSize(pageSize)
  }

  useEffect(() => {
    setUsersList(data.map((item) => {
      let status = ''

      if (item.blocked) {
        status = 'blocked'
      } else if (item.confirmed) {
        status = 'confirmed'
      } else if (item.invited_at) {
        status = 'invitedAt'
      } else {
        status = 'registered'
      }
      let profileImageUrl = null
      if (item.profileImage) {
        profileImageUrl = item.profileImage.url.indexOf('http') === 0
          ? item.profileImage.url
          : `/api/ums${item.profileImage.url}`
      }
      return {
        key: item.guid,
        username: item.first_name + ' ' + item.last_name,
        email: item.email,
        status: status,
        role: item.role ? item.role.name : '',
        profileImageUrl: profileImageUrl
      }
    }))
  }, [data])

  const renderStatusMenu = (value, record) => {
    const menuItems = statusMenu.map((item, i) => {
      let menuItem = null

      if (value === 'confirmed' && item.label === 'Deactivate account') {
        menuItem = (
                    <Menu.Item onClick={() => item.onClick(false, record.key)} key={item.label + i + 'zzz'}>
                        {item.label}
                    </Menu.Item>
        )
      }

      if (value === 'blocked' && item.label === 'Reactivate account') {
        menuItem = (
                    <Menu.Item onClick={() => item.onClick(true, record.key)} key={item.label + i + 'zzz'}>
                        {item.label}
                    </Menu.Item>
        )
      }

      if ((value === 'invitedAt') && item.label === 'Resend invite') {
        menuItem = (
                    <Menu.Item onClick={() => item.onClick(true, record.key)} key={item.label + i + 'zzz'}>
                        {item.label}
                    </Menu.Item>
        )
      }

      return menuItem
    })
    if (value === 'registered') {
      return null
    }
    return (
            <Menu>
                {menuItems}
            </Menu>
    )
  }

  const renderResetAndDeleteMenu = () => {
    const menuItems = resetAndDeleteMenu.map((item, i) => {
      let menuItem = null
      if (item.label === 'Reset password') {
        menuItem = (
                    <Menu.Item
                        onClick={() => item.onClick(true, i.key)}
                        key={item.label + i}
                    >
                        {item.label}
                    </Menu.Item>
        )
      }

      if (item.label === 'Delete user') {
        menuItem = (
                    <Menu.Item
                        className={styles.delete_user}
                        onClick={() => item.onClick(true, i.key)}
                        key={item.label + i}
                    >
                        {item.label}
                    </Menu.Item>
        )
      }

      return menuItem
    })

    return (
            <Menu>
                {menuItems}
            </Menu>
    )
  }

  const renderRoleMenu = (value, record) => {
    const menuItems = roles.map((item, i) =>
            <Menu.Item
                onClick={() => item.onClick(item.id, record.key)}
                key={item.label + i}
            >
                {item.label}
            </Menu.Item>
    )

    return (
            <Menu>
                {menuItems}
            </Menu>
    )
  }

  const renderStatus = (value, record) => {
    let status = 'Invite pending'
    let style = 'warning'

    switch (value) {
      case 'confirmed':
        status = 'Active'
        style = 'success'
        break
      case 'blocked':
        status = 'Deactivated'
        style = 'danger'
        break
      case 'registered':
        status = 'Registered'
        style = 'warning'
        break
      default:
        break
    }
    const statusDropdownItems = renderStatusMenu(value, record)

    if (!statusDropdownItems) {
      return (
        <span className={[styles.statusLabel, styles[style]].join(' ')}>
          {status}
        </span>
      )
    }
    return (
            <Dropdown overlay={statusDropdownItems} overlayClassName={styles.DropdownMenu} trigger={['click']}>
                <a
                    href='/'
                    className={['ant-dropdown-link', styles[style], styles.statusDropdown].join(' ')}
                    onClick={e => e.preventDefault()}
                >
                    <span className={[styles.statusLabel, styles[style]].join(' ')}>
                        {status} <IconArrowDown size={'sm'} />
                    </span>
                </a>
            </Dropdown>
    )
  }

  const renderRole = (value, record) => {
    return (
            <div className={styles.user_role_container}>
                <Dropdown overlay={renderRoleMenu(value, record)} overlayClassName={styles.DropdownMenu} trigger={['click']}>
                    <a
                        href='/'
                        className={['ant-dropdown-link', styles.statusDropdown].join(' ')}
                        onClick={e => e.preventDefault()}
                    >
                        <span>{value} <IconArrowDown size={'sm'} fill={'var(--black-80)'}/></span>
                    </a>
                </Dropdown>
                <Dropdown overlay={renderResetAndDeleteMenu(data)} overlayClassName={styles.DropdownMenu} trigger={['click']}>
                    <a
                        href='/'
                        className={['ant-dropdown-link', styles.statusDropdown].join(' ')}
                        onClick={e => e.preventDefault()}
                    >
                        <IconEditPen size={'sm'} fill={'var(--black-60)'} />
                    </a>
                </Dropdown>
            </div>
    )
  }

  const renderText = value => <span>{value}</span>

  const load = useCallback(() => {
    getUsersDataList()
    getUsersTotal()
  }, [getUsersDataList, getUsersTotal])

  useEffect(load, [load, lastUpdatedAt])

  const columns = [
    {
      title: '',
      dataIndex: 'profileImageUrl',
      align: 'center',
      width: 100,
      render: function renderImage (value) {
        return <div className={styles.username_container}>
              <Avatar size={'x'} src={value || '/img_avatar.png'} alt="user logo"/>
          </div>
      }
    },
    {
      title: 'Name',
      dataIndex: 'username',
      align: 'left',
      render: renderText
    },
    {
      title: 'Email',
      dataIndex: 'email',
      render: renderText
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 250,
      render: renderStatus
    },
    {
      title: 'Role',
      dataIndex: 'role',
      render: renderRole
    }
  ]

  const onRowClick = (record) => {
    return {
      onClick: () => {
        setUserEmail(record.email ? record.email : '')
        setUserName(record.username ? record.username : '')
        setUserGuid(record.key)
      }
    }
  }

  return (
        <div className={styles.user_data_container}>
            <CardContainer>
                <div className={styles.list_top}>
                    <h1 className={styles.title}>Users</h1>
                </div>
                <Table
                    onRow={onRowClick}
                    columns={columns}
                    dataSource={usersList}
                    className={styles.table}
                    pagination={{
                      defaultPageSize: DEFAULT_PAGE_SIZE,
                      pageSizeOptions: DEFAULT_PAGE_SIZE_OPTIONS,
                      showLessItems: true,
                      total: userDataTotal,
                      onChange: onTablePaginationChange
                    }}
                    sticky
                    size='small'
                    locale={{ emptyText: <NoDataAvailable /> }}
                />

            </CardContainer>
        </div>
  )
}

export default UserDataList
